'use client'

import { cn } from '@/lib/utils'
import { Fragment, memo, useRef, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../app/(public)/sim-racing-events/components/command'

// TODO Fix search issue when selecting and then deselecting an option
// TODO Support for forms
/**
 * Search Element - Styled search element for ComboBox like functionality within a command (like airbnb and others)
 * @param {object[] || string[]} options - Options for the CommandList
 * @param {string} value - Value of the Select
 * @param {string} valueKey - Key of the value (option[valueKey])
 * @param {string} labelKey - Key of the label (option[labelKey])
 * @param {function} setValue - Function to set the value
 * @param {boolean} isMulti - Is the element multi-select
 * @returns {jsx} Search Element
 */
function SearchElement({
  options,
  value,
  valueKey,
  labelKey,
  setValue,
  isMulti,
  grouped,
  groups,
}) {
  const [isFocused, setIsFocused] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef(null)

  const handleSelect = (cv) => {
    setValue(cv)
    setSearchQuery('')

    if (isMulti) {
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
      return
    }

    setIsFocused(false)
    inputRef.current?.blur()
  }

  // Manual filtering to handle spaces and maintain group integrity
  const filterOptions = (items) => {
    if (!searchQuery.trim()) return items

    const query = searchQuery.toLowerCase().trim()
    return items.filter((item) => {
      const label = labelKey ? item[labelKey] : item
      return label.toLowerCase().includes(query)
    })
  }

  return (
    <Command
      className={cn('relative overflow-visible transition-all ease-in-out', {
        'desktop-xs:hover:bg-[#dbdcdd]': !isFocused,
      })}
      onBlur={() => {
        setIsFocused(false)
        setSearchQuery('')
      }}
      onFocus={() => setIsFocused(true)}
      open={false}
      defaultValue={'-'}
      shouldFilter={false}
    >
      <div className="group">
        <div
          className={cn(
            'pointer-events-none absolute z-10 flex w-[80%] select-none items-center rounded-md bg-shades-0 transition-all ease-in-out desktop-xs:h-[57px]',
            {
              'opacity-0': isFocused,
              'desktop-xs:group-hover:bg-[#dbdcdd]': !isFocused,
            },
          )}
        >
          <p className="outline-hidden flex h-11 w-full select-none truncate rounded-sm py-3 pl-3 text-p-2 font-semibold text-neutral-blue-900 placeholder:text-neutral-blue-500 disabled:cursor-not-allowed disabled:opacity-50">
            {isMulti
              ? value?.length > 0
                ? valueKey && labelKey
                  ? options
                      .filter((o) => value.some((v) => o[valueKey] === v))
                      .map((o) => o[labelKey])
                      .join(', ')
                  : value.join(', ')
                : 'Any'
              : valueKey && labelKey && value && value !== ''
                ? options.find((o) => o[valueKey] === value)?.[labelKey] ||
                  value
                : value || 'Any'}
          </p>
        </div>

        <CommandInput
          ref={inputRef}
          placeholder="Search..."
          isOpen={isFocused}
          onClick={() => setIsFocused(true)}
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
      </div>

      <CommandList
        className={cn(
          'white-scrollbar absolute top-[85%] z-20 w-full transition-all ease-in-out',
          {
            'pointer-events-none opacity-0': !isFocused, // Replace hidden with opacity and disable pointer events
            'pointer-events-auto opacity-100': isFocused,
            'rounded-b-md bg-white shadow-lg shadow-neutral-blue-900/20':
              isFocused,
          },
        )}
      >
        {grouped && groups.length > 0 ? (
          <div className="h-2" />
        ) : (
          <div className="mx-4 my-2 h-px rounded-lg bg-neutral-blue-200" />
        )}
        <CommandEmpty>None found.</CommandEmpty>

        {grouped && groups.length > 0 ? (
          <>
            {groups.map((group, index) => {
              const groupOptions = options.filter(
                (option) => option[group.prop] === group.compareVal,
              )
              const filteredGroupOptions = filterOptions(groupOptions)

              return (
                <Fragment key={index}>
                  {filteredGroupOptions.length > 0 && (
                    <>
                      {group.label !== false && (
                        <>
                          <div className="divide mb-2 ml-4 w-[calc(100%-26px)] bg-neutral-blue-200" />
                          <p
                            key={index}
                            className="pl-4 text-xs font-semibold text-neutral-blue-500"
                          >
                            {group.label}
                          </p>
                        </>
                      )}
                      {filteredGroupOptions.map((o, optionIndex) => (
                        <CommandItem
                          value={valueKey ? o[valueKey] : o}
                          onSelect={(cv) => handleSelect(cv)}
                          key={
                            valueKey
                              ? o[valueKey] + optionIndex
                              : o + optionIndex
                          }
                        >
                          <div
                            className={cn('h-full w-1 bg-primary opacity-0', {
                              'opacity-100': isMulti
                                ? value?.some(
                                    (v) => v === (valueKey ? o[valueKey] : o),
                                  )
                                : value === (valueKey ? o[valueKey] : o),
                            })}
                          />

                          <p className="text-p-2 font-medium">
                            {labelKey ? o[labelKey] : o}
                          </p>
                        </CommandItem>
                      ))}
                    </>
                  )}
                </Fragment>
              )
            })}
          </>
        ) : (
          <>
            {filterOptions(options).map((o) => (
              <CommandItem
                value={valueKey ? o[valueKey] : o}
                onSelect={(cv) => handleSelect(cv)}
                key={valueKey ? o[valueKey] : o}
              >
                <div
                  className={cn('h-full w-1 bg-primary opacity-0', {
                    'opacity-100': isMulti
                      ? value?.some((v) => v === (valueKey ? o[valueKey] : o))
                      : value === (valueKey ? o[valueKey] : o),
                  })}
                />

                <p className="text-p-2 font-medium">
                  {labelKey ? o[labelKey] : o}
                </p>
              </CommandItem>
            ))}
          </>
        )}
      </CommandList>
    </Command>
  )
}

export default memo(SearchElement)
